import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Skeleton } from '@/components/ui/skeleton'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { toProfile } from '@/lib/link'
import { generateImageByPubkey } from '@/lib/pubkey'
import { randomString } from '@/lib/random'
import { cn } from '@/lib/utils'
import { SecondaryPageLink } from '@/PageManager'
import modalManager from '@/services/modal-manager.service'
import { NostrUser } from '@nostr/gadgets/metadata'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Image from './Image'
import ProfileCard from './ProfileCard'
import { useAtomValue } from 'jotai'
import { avatarStyleAtom } from '@/atoms/theme'

const UserAvatarSizeCnMap = {
  large: 'w-24 h-24',
  big: 'w-16 h-16',
  semiBig: 'w-12 h-12',
  normal: 'w-10 h-10',
  medium: 'w-9 h-9',
  small: 'w-7 h-7',
  xSmall: 'w-5 h-5',
  tiny: 'w-4 h-4'
}

export default function UserAvatar({
  user,
  className,
  size = 'normal'
}: {
  user: string | NostrUser
  className?: string
  size?: 'large' | 'big' | 'semiBig' | 'normal' | 'medium' | 'small' | 'xSmall' | 'tiny'
}) {
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <SecondaryPageLink
          to={toProfile(typeof user === 'object' ? user.npub : user)}
          onClick={(e) => e.stopPropagation()}
        >
          <SimpleUserAvatar user={user} size={size} className={className} />
        </SecondaryPageLink>
      </HoverCardTrigger>
      <HoverCardContent className="w-72">
        <ProfileCard user={user} />
      </HoverCardContent>
    </HoverCard>
  )
}

const UserAvatarModifiersMap: Record<string, string> = {
  large: 'rs:fill:96:96',
  big: 'rs:fill:64:64',
  semiBig: 'rs:fill:48:48',
  normal: 'rs:fill:40:40',
  medium: 'rs:fill:36:36',
  small: 'rs:fill:28:28',
  xSmall: 'rs:fill:20:20',
  tiny: 'rs:fill:16:16'
}

export function SimpleUserAvatar({
  user,
  size = 'normal',
  className,
  onClick
}: {
  user: string | NostrUser
  size?: 'large' | 'big' | 'semiBig' | 'normal' | 'medium' | 'small' | 'xSmall' | 'tiny'
  className?: string
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}) {
  const { profile } = useFetchProfile(user)
  const defaultAvatar = useMemo(
    () => (profile?.pubkey ? generateImageByPubkey(profile.pubkey) : ''),
    [profile]
  )
  const avatarStyle = useAtomValue(avatarStyleAtom)
  const rounded = avatarStyle === 'circle' ? 'rounded-full' : 'rounded-sm'

  if (!profile) {
    return <Skeleton className={cn('shrink-0', UserAvatarSizeCnMap[size], rounded, className)} />
  }

  return (
    <Image
      imageUrl={profile.metadata?.picture}
      imagePubkey={profile.pubkey}
      placeholder={defaultAvatar}
      className="object-cover object-center"
      classNames={{
        wrapper: cn('shrink-0 bg-background', UserAvatarSizeCnMap[size], rounded, className)
      }}
      imgproxyModifiers={UserAvatarModifiersMap[size]}
      onClick={onClick}
    />
  )
}

export function AvatarWithLightbox({
  user,
  size = 'normal',
  className
}: {
  user: string | NostrUser
  size?: 'large' | 'big' | 'semiBig' | 'normal' | 'medium' | 'small' | 'xSmall' | 'tiny'
  className?: string
}) {
  const id = useMemo(() => `user-avatar-lightbox-${randomString()}`, [])
  const { profile } = useFetchProfile(user)
  const defaultAvatar = useMemo(
    () => (profile?.pubkey ? generateImageByPubkey(profile.pubkey) : ''),
    [profile]
  )
  const [index, setIndex] = useState(-1)

  useEffect(() => {
    if (index >= 0) {
      modalManager.register(id, () => {
        setIndex(-1)
      })
    } else {
      modalManager.unregister(id)
    }
  }, [index, id])

  const imageUrl = profile?.metadata?.picture ?? defaultAvatar

  return (
    <>
      <SimpleUserAvatar
        user={user}
        size={size}
        className={cn('cursor-zoom-in', className)}
        onClick={handleClick}
      />
      {index >= 0 &&
        createPortal(
          <div onClick={(e) => e.stopPropagation()}>
            <Lightbox
              index={index}
              slides={[{ src: imageUrl }]}
              plugins={[Zoom]}
              open={index >= 0}
              close={() => setIndex(-1)}
              controller={{
                closeOnBackdropClick: true,
                closeOnPullUp: true,
                closeOnPullDown: true
              }}
              carousel={{
                finite: true
              }}
              render={{
                buttonPrev: () => null,
                buttonNext: () => null
              }}
              styles={{
                toolbar: { paddingTop: '2.25rem' }
              }}
            />
          </div>,
          document.body
        )}
    </>
  )

  function handleClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation()
    e.preventDefault()
    setIndex(0)
  }
}
